import { supabase, isSupabaseConfigured } from '@/lib/supabase'
import { chapterMap, getChapter } from '@/data/chapters'
import type { ChapterStatus } from '@/types/storytelling'

interface ChapterStatusEntry {
  chapterId: string
  status: ChapterStatus
}

interface ProgressRow {
  child_id: string
  chapter_id: string
  status: ChapterStatus
  completed_at: string | null
}

/**
 * Compute chapter statuses from DB progress rows.
 * Pure function — no DB calls.
 */
export function getChapterStatuses(progress: ProgressRow[]): ChapterStatusEntry[] {
  const completedIds = new Set(
    progress.filter((p) => p.status === 'completed').map((p) => p.chapter_id)
  )

  return chapterMap.map((chapter, index) => {
    if (completedIds.has(chapter.id)) {
      return { chapterId: chapter.id, status: 'completed' as const }
    }

    const isFirstChapter = index === 0
    const previousCompleted = index > 0 && completedIds.has(chapterMap[index - 1].id)

    if (isFirstChapter || previousCompleted) {
      return { chapterId: chapter.id, status: 'active' as const }
    }

    return { chapterId: chapter.id, status: 'locked' as const }
  })
}

/**
 * Get playgrounds unlocked by completed chapters.
 * Pure function.
 */
export function getUnlockedPlaygrounds(completedChapterIds: string[]): string[] {
  const playgrounds: string[] = []
  for (const id of completedChapterIds) {
    const chapter = getChapter(id)
    if (chapter?.unlocksPlayground) {
      playgrounds.push(chapter.unlocksPlayground)
    }
  }
  return playgrounds
}

/**
 * Default initial progress (first chapter active).
 */
export function initializeProgress(): ChapterStatusEntry[] {
  return [{ chapterId: 'the-hook', status: 'active' }]
}

/**
 * Load progress from Supabase for a child.
 */
export async function loadProgress(childId: string): Promise<ProgressRow[]> {
  if (!isSupabaseConfigured()) return []

  const { data, error } = await supabase
    .from('storytelling_progress')
    .select('*')
    .eq('child_id', childId)

  if (error) {
    console.error('Failed to load storytelling progress:', error)
    return []
  }

  return data ?? []
}

/**
 * Mark a chapter as completed and persist to Supabase.
 */
export async function completeChapter(childId: string, chapterId: string): Promise<boolean> {
  if (!isSupabaseConfigured()) return false

  const { error } = await supabase
    .from('storytelling_progress')
    .upsert(
      {
        child_id: childId,
        chapter_id: chapterId,
        status: 'completed',
        completed_at: new Date().toISOString(),
      },
      { onConflict: 'child_id,chapter_id' }
    )

  if (error) {
    console.error('Failed to complete chapter:', error)
    return false
  }

  return true
}
